import firebase from "firebase/compat";
import { isEmpty } from "ramda";
import { FC, useEffect, useRef, useState } from "react";
import {
  Create,
  CreateProps,
  FormGroupContextProvider,
  FormWithRedirect,
  maxValue,
  minValue,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  useLoading,
} from "react-admin";
import { Field } from "react-final-form";
import { useSelector } from "react-redux";

import CustomCard from "../../components/CustomCard";
import CustomSelect from "../../components/CustomSelect";
import CustomToolbar from "../../components/CustomToolbar";
import FormWrapper from "../../components/FormWrapper";
import ImageUploadInput from "../../components/ImageUploadInput";
import Location from "../../components/Location";
import Spacing from "../../components/Spacing";
import useCurrentUser from "../../hooks/useCurrentUser";
import useDisabledOnLoading from "../../hooks/useDisabledOnLoading";
import { GenericObject } from "../../types/generic.types";
import { FormGroupNames } from "../../types/reactAdmin.types";
import { ReduxState } from "../../types/redux.types";
import { AppResource, CourtsAvailabilityStatus, CourtsTypes, IEnums } from "../../types/resource.types";
import { validateFilesCount, validateFileSize, validateIsIntegerNumber } from "../../utils/validators";
import { createCourtMinSlotTime } from "./utils";
import useCourtsLocalizations from "./utils/useCourtsLocalizations";

const courtMinSlotTime = createCourtMinSlotTime(10);

const CustomForm: FC<any> = (props) => {
  const { minReservationSlotRef, minSlotRef } = props;
  const loading = useLoading();
  const settings = useSelector<ReduxState, Record<string, any>>((state) => state.settings);

  const { disabledStyles } = useDisabledOnLoading(loading);
  const storeEnums = useSelector<ReduxState, IEnums>((state) => state.enums);
  const localizations = useCourtsLocalizations(props);

  const [minSlot, setMinSlot] = useState("");
  const [minReservationSlot, setMinReservationSlot] = useState("");
  const [minReservationSlotsList, setMinReservationSlotsList] = useState<any[]>([]);

  const handleSelectMinReservationSlot = (selectedItem: string) => {
    setMinReservationSlot(selectedItem.toString());
    minReservationSlotRef.current = selectedItem;
  };

  useEffect(() => {
    if (settings && settings.general) {
      const settingsMinReservationSlot = settings.general.maxReservationSlots;
      const list: GenericObject[] = [];
      for (let i = 0; i < settingsMinReservationSlot; i++) {
        list.push({ id: i + 1, name: i + 1 });
      }
      setMinReservationSlotsList(list);
    }
  }, [settings]);

  return (
    <FormWrapper>
      <FormGroupContextProvider name={FormGroupNames.courtsCreate}>
        <FormWithRedirect
          {...props}
          redirect={`/${AppResource.courts}`}
          render={(formProps) => {
            return (
              <form onSubmit={formProps.handleSubmit} style={disabledStyles}>
                <CustomCard title="Court Details">
                  <Spacing>
                    {localizations?.[0]}
                    {localizations?.[1]}
                  </Spacing>

                  <Spacing>
                    {localizations?.[2]}
                    {localizations?.[3]}
                  </Spacing>
                  <Spacing>
                    <CustomSelect
                      selectedItem={minSlot}
                      handleSelectItem={(selectedItem: string) => {
                        minSlotRef.current = selectedItem;
                        setMinSlot(selectedItem.toString());
                      }}
                      labelTitle="Default slot time"
                      listItems={courtMinSlotTime}
                    />
                    <CustomSelect
                      selectedItem={minReservationSlot}
                      handleSelectItem={handleSelectMinReservationSlot}
                      labelTitle="Min Reservation Slot"
                      listItems={minReservationSlotsList}
                    />
                  </Spacing>
                  <Spacing>
                    <NumberInput
                      source="pricePerHour"
                      variant="outlined"
                      fullWidth
                      validate={[required(), minValue(0), maxValue(5000)]}
                      label="Price per hour"
                    />
                    <NumberInput
                      source="maxPlayers"
                      variant="outlined"
                      fullWidth
                      validate={[required(), minValue(1), maxValue(500), validateIsIntegerNumber()]}
                    />
                  </Spacing>

                  <Spacing>
                    <ReferenceInput
                      label="Category"
                      source="courtCategoryId"
                      reference={AppResource.courtsCategories}
                      variant="outlined"
                      validate={required()}
                      fullWidth
                    >
                      <SelectInput optionText="title.en" />
                    </ReferenceInput>

                    <SelectInput
                      variant="outlined"
                      fullWidth
                      source="courtsAvailabilitiesStatus"
                      optionText="value.label.en"
                      validate={required()}
                      choices={
                        !isEmpty(storeEnums)
                          ? [
                              {
                                id: CourtsAvailabilityStatus.busy,
                                value: storeEnums.courtsAvailabilitiesStatus[CourtsAvailabilityStatus.busy],
                              },
                              {
                                id: CourtsAvailabilityStatus.available,
                                value: storeEnums.courtsAvailabilitiesStatus[CourtsAvailabilityStatus.available],
                              },
                              {
                                id: CourtsAvailabilityStatus.maintenance,
                                value: storeEnums.courtsAvailabilitiesStatus[CourtsAvailabilityStatus.maintenance],
                              },
                            ]
                          : []
                      }
                    />
                  </Spacing>

                  <SelectInput
                    variant="outlined"
                    fullWidth
                    source="type"
                    optionText="value.label.en"
                    validate={required()}
                    choices={
                      !isEmpty(storeEnums)
                        ? [
                            {
                              id: CourtsTypes.coveredHall,
                              value: storeEnums.courtsTypes[CourtsTypes.coveredHall],
                            },
                            {
                              id: CourtsTypes.openHall,
                              value: storeEnums.courtsTypes[CourtsTypes.openHall],
                            },
                          ]
                        : []
                    }
                  />
                </CustomCard>

                <CustomCard title="Images">
                  <ImageUploadInput
                    source="images"
                    validate={[required(), validateFileSize("images"), validateFilesCount("images")]}
                    multiple
                  />
                </CustomCard>

                <CustomCard title="Location">
                  <Field name="location" validate={required()}>
                    {(fieldProps) => (
                      <Location
                        onLocationChange={(data) => {
                          if (data?.lat && data?.lng) {
                            const geoPoint = new firebase.firestore.GeoPoint(data.lat, data.lng);
                            fieldProps.input.onChange(geoPoint);
                          }
                        }}
                      />
                    )}
                  </Field>
                </CustomCard>

                <CustomToolbar
                  {...formProps}
                  formName={FormGroupNames.courtsCreate}
                  customErrors={{
                    minSlot: minSlot ? "" : "Min slot is required",
                    minReservationSlot: minReservationSlot ? "" : "Min Reservation Slot is required",
                  }}
                />
              </form>
            );
          }}
        />
      </FormGroupContextProvider>
    </FormWrapper>
  );
};

const CourtsCreate: FC<CreateProps> = (props) => {
  const { user } = useCurrentUser();
  const minSlotRef = useRef("");
  const minReservationSlotRef = useRef("");

  return (
    <Create
      {...props}
      component="section"
      transform={(values) => {
        values.sportClubId = user.id;
        values.defaultSlotTime = +minSlotRef.current;
        values.minReservationSlot = +minReservationSlotRef.current;
        console.log({ values });
        return values;
      }}
    >
      <CustomForm minReservationSlotRef={minReservationSlotRef} minSlotRef={minSlotRef} />
    </Create>
  );
};

export default CourtsCreate;
